import React, { useState, useCallback, useEffect } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
    TextInput,
    ScrollView,
    RefreshControl,
} from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { theme } from '../constants/theme';
import { ScreenHeader } from '../components/ScreenHeader';
import { CowTransactionItem, ItemSeparator } from '../components/CowTransactionItem';
import { SkeletonList } from '../components/ShimmerLoading';
import ApiService from '../services/ApiService';
import { Cow } from '../models/types';

const CowListScreen = ({ navigation }: any) => {
    const [cows, setCows] = useState<Cow[]>([]);
    const [filteredCows, setFilteredCows] = useState<Cow[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [filterStatus, setFilterStatus] = useState<
        'all' | 'active' | 'sold' | 'deceased' | 'pregnant'
    >('all');

    const loadCows = async (showLoader = true) => {
        try {
            if (showLoader && isInitialLoad) {
                setLoading(true);
            }
            const allCows = await ApiService.getCows();
            setCows(allCows);
        } catch (error) {
            console.error('Error loading cows:', error);
        } finally {
            setLoading(false);
            setIsInitialLoad(false);
        }
    };

    const onRefresh = async () => {
        setRefreshing(true);
        await loadCows(false);
        setRefreshing(false);
    };

    useFocusEffect(
        useCallback(() => {
            loadCows();
        }, []),
    );

    useEffect(() => {
        if (cows.length > 0) {
            applyFilters(cows, searchQuery, filterStatus);
        }
    }, [cows, searchQuery, filterStatus]);

    const applyFilters = (
        data: Cow[],
        query: string,
        status: 'all' | 'active' | 'sold' | 'deceased' | 'pregnant',
    ) => {
        let filtered = data;

        // Filter by status
        if (status === 'pregnant') {
            filtered = filtered.filter(cow => cow.isPregnant && cow.currentStatus === 'active');
        } else if (status !== 'all') {
            filtered = filtered.filter(cow => cow.currentStatus === status);
        }

        // Filter by search query
        if (query.trim()) {
            const lowerQuery = query.toLowerCase();
            filtered = filtered.filter(
                cow =>
                    cow.id.toLowerCase().includes(lowerQuery) ||
                    cow.name?.toLowerCase().includes(lowerQuery) ||
                    cow.breed?.toLowerCase().includes(lowerQuery),
            );
        }

        setFilteredCows(filtered);
    };

    const handleSearch = (query: string) => {
        setSearchQuery(query);
    };

    const handleFilterChange = (
        status: 'all' | 'active' | 'sold' | 'deceased' | 'pregnant',
    ) => {
        setFilterStatus(status);
    };

    const renderCowItem = ({ item }: { item: Cow }) => (
        <CowTransactionItem cow={item} onPress={() => navigation.navigate('CowDetail', { cow: item })} />
    );

    const renderEmpty = () => (
        <View style={styles.emptyContainer}>
            <View style={styles.emptyIconContainer}>
                <Icon name="cow" size={64} color={theme.colors.textLight} />
            </View>
            <Text style={styles.emptyText}>
                {cows.length === 0 ? 'No cows found' : 'No cows match this filter'}
            </Text>
            <Text style={styles.emptySubtext}>
                {cows.length === 0 ? 'Add your first cow to get started' : 'Try selecting a different filter'}
            </Text>
            {cows.length === 0 && (
                <TouchableOpacity
                    style={styles.emptyButton}
                    onPress={() => navigation.navigate('AddEditCow')}>
                    <Icon name="plus" size={20} color={theme.colors.surface} />
                    <Text style={styles.emptyButtonText}>Add Cow</Text>
                </TouchableOpacity>
            )}
        </View>
    );

    const getFilterCount = (status: string) => {
        if (status === 'all') return cows.length;
        if (status === 'pregnant') return cows.filter(cow => cow.isPregnant && cow.currentStatus === 'active').length;
        return cows.filter(cow => cow.currentStatus === status).length;
    };

    return (
        <SafeAreaView style={styles.container}>
            <ScreenHeader title="Cows" />

            <View style={styles.searchContainer}>
                <Icon
                    name="magnify"
                    size={20}
                    color={theme.colors.textSecondary}
                    style={styles.searchIcon}
                />
                <TextInput
                    style={styles.searchInput}
                    placeholder="Search by ID, name, or breed..."
                    value={searchQuery}
                    onChangeText={handleSearch}
                    placeholderTextColor={theme.colors.textLight}
                />
                {searchQuery.length > 0 && (
                    <TouchableOpacity onPress={() => handleSearch('')}>
                        <Icon name="close-circle" size={20} color={theme.colors.textSecondary} />
                    </TouchableOpacity>
                )}
            </View>

            {/* Rounded Tab Container */}
            <View style={styles.tabContainer}>
                <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={styles.filterScrollContent}
                    style={styles.filterScroll}>
                    {[
                        { key: 'all', label: 'All', icon: 'view-grid', color: theme.colors.primary },
                        { key: 'active', label: 'Active', icon: 'cow', color: '#4CAF50' },
                        { key: 'pregnant', label: 'Pregnant', icon: 'heart-pulse', color: '#E91E63' },
                        { key: 'sold', label: 'Sold', icon: 'currency-inr', color: '#FF9800' },
                        { key: 'deceased', label: 'Deceased', icon: 'grave-stone', color: '#9E9E9E' },
                    ].map(({ key, label, icon, color }) => {
                        const count = getFilterCount(key);
                        const isActive = filterStatus === key;
                        return (
                            <TouchableOpacity
                                key={key}
                                style={[
                                    styles.filterButton,
                                    isActive && { backgroundColor: color, borderColor: color },
                                ]}
                                onPress={() =>
                                    handleFilterChange(key as 'all' | 'active' | 'sold' | 'deceased' | 'pregnant')
                                }
                                activeOpacity={0.7}>
                                <Icon
                                    name={icon}
                                    size={16}
                                    color={isActive ? theme.colors.surface : color}
                                    style={styles.filterIcon}
                                />
                                <Text
                                    style={[
                                        styles.filterButtonText,
                                        { color: isActive ? theme.colors.surface : theme.colors.text },
                                    ]}>
                                    {label}
                                </Text>
                                <View style={[
                                    styles.filterBadge,
                                    { backgroundColor: isActive ? 'rgba(255,255,255,0.3)' : color + '20' },
                                ]}>
                                    <Text style={[
                                        styles.filterBadgeText,
                                        { color: isActive ? theme.colors.surface : color },
                                    ]}>{count}</Text>
                                </View>
                            </TouchableOpacity>
                        );
                    })}
                </ScrollView>
            </View>

            {/* Transaction-style List in Rounded Container */}
            {loading && isInitialLoad ? (
                <View style={styles.skeletonWrapper}>
                    <SkeletonList itemCount={5} />
                </View>
            ) : (
                <FlashList
                    data={filteredCows}
                    renderItem={renderCowItem}
                    keyExtractor={item => item.id}
                    contentContainerStyle={styles.listContainer}
                    ListEmptyComponent={renderEmpty}
                    showsVerticalScrollIndicator={true}
                    ItemSeparatorComponent={ItemSeparator}
                    refreshControl={
                        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
                    }
                />
            )}

            {/* FAB */}
            <TouchableOpacity
                style={styles.fab}
                onPress={() => navigation.navigate('AddEditCow')}
            >
                <Icon name="plus" size={28} color="#fff" />
            </TouchableOpacity>
        </SafeAreaView >
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    header: {
        backgroundColor: theme.colors.primary,
        paddingVertical: theme.spacing.sm,
        paddingHorizontal: theme.spacing.md,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        ...theme.shadows.sm,
    },
    headerSpacer: {
        width: 38,
    },
    title: {
        ...theme.typography.h4,
        color: theme.colors.surface,
        fontWeight: '700',
        textAlign: 'center',
        flex: 1,
    },

    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        margin: theme.spacing.md,
        marginBottom: theme.spacing.sm,
        paddingHorizontal: theme.spacing.md,
        ...theme.shadows.sm,
    },
    searchIcon: {
        marginRight: theme.spacing.sm,
    },
    searchInput: {
        flex: 1,
        ...theme.typography.body1,
        color: theme.colors.text,
        paddingVertical: theme.spacing.md,
    },
    tabContainer: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.xl,
        marginHorizontal: theme.spacing.md,
        marginBottom: theme.spacing.md,
        paddingVertical: theme.spacing.sm,
        ...theme.shadows.sm,
    },
    filterScroll: {
        maxHeight: 50,
    },
    filterScrollContent: {
        paddingHorizontal: theme.spacing.md,
        gap: theme.spacing.sm,
    },
    filterButton: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingLeft: theme.spacing.sm,
        paddingRight: theme.spacing.sm,
        paddingVertical: theme.spacing.xs + 2,
        borderRadius: theme.borderRadius.round,
        backgroundColor: theme.colors.surface,
        borderWidth: 1.5,
        borderColor: theme.colors.border,
        gap: theme.spacing.xs,
        ...theme.shadows.sm,
    },
    filterIcon: {
        marginRight: 2,
    },
    filterButtonText: {
        ...theme.typography.body2,
        fontWeight: '600',
    },
    filterBadge: {
        borderRadius: theme.borderRadius.round,
        minWidth: 22,
        height: 22,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 6,
    },
    filterBadgeText: {
        fontWeight: '700',
        fontSize: 12,
    },
    listWrapper: {
        flex: 1,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.xl,
        // marginHorizontal: theme.spacing.md,
        marginBottom: theme.spacing.md,
        overflow: 'hidden',
        ...theme.shadows.sm,
    },
    listContainer: {
        flexGrow: 1,
    },
    emptyContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: theme.spacing.xxl,
    },
    emptyIconContainer: {
        width: 100,
        height: 100,
        borderRadius: 50,
        backgroundColor: theme.colors.background,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: theme.spacing.md,
        ...theme.shadows.sm,
    },
    emptyText: {
        ...theme.typography.h5,
        color: theme.colors.textSecondary,
        marginTop: theme.spacing.sm,
    },
    emptySubtext: {
        ...theme.typography.body2,
        color: theme.colors.textLight,
        marginTop: theme.spacing.xs,
    },
    emptyButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.primary,
        paddingHorizontal: theme.spacing.lg,
        paddingVertical: theme.spacing.md,
        borderRadius: theme.borderRadius.round,
        marginTop: theme.spacing.lg,
        ...theme.shadows.md,
    },
    emptyButtonText: {
        ...theme.typography.button,
        color: theme.colors.surface,
        marginLeft: theme.spacing.sm,
    },
    fab: {
        position: 'absolute',
        right: theme.spacing.lg,
        bottom: theme.spacing.lg,
        width: 56,
        height: 56,
        borderRadius: 28,
        backgroundColor: theme.colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
        ...theme.shadows.md,
    },
    skeletonWrapper: {
        flex: 1,
        paddingHorizontal: theme.spacing.md,
        paddingVertical: theme.spacing.md,
    },
});

export default CowListScreen;
